import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { DeviceEventEmitter, Platform, StyleSheet, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useFocusEffect, useLinkTo } from "@react-navigation/native";
import { ChatWidget, ChatWidgetRef } from "vdb-ai-chat";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { RedirectFunc } from "@scenes/configurator/OrderCart/utils";
import LineViewContainer from "@scenes/requests/utils/LineView";
import {
  AIEventNames,
  AIEventTypes,
} from "@scenes/authentication/utils/Types/types";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import AiChatHeader from "@scenes/ai-chatbot/ChatHeader";
import { ProductCategory } from "@utils/constants";
import { DefaultTheme, useTheme } from "styled-components/native";
import styled from "styled-components/native";
import { getBoxShadow } from "@utils/product/productValidators";
import { css } from "styled-components/native";
import { LinearGradient } from "expo-linear-gradient";
import {
  clearSubCollections,
  resetFiltersPayloadByProduct,
  resetJewelryFiltersPayload,
  useSearchFiltersPayload,
  useSearchFiltersSlice,
} from "@redux/slices/searchFilters";
import { SpaceType } from "../Spacer/types";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { FilterScreenContext } from "@scenes/explore/FilterScreenContext";
import { SearchFilterTypeEnum } from "../Filters/utils";
import { fetchLocalStorageItems } from "@utils/index";
import { useAIChatBotConfig } from "@redux/slices/globalSlice";

const TOOLTIP_WIDTH = 420;
const TOOLTIP_HEIGHT = 600;

type ChatTooltipProps = {
  isOpen: boolean;
  onClose: () => void;
  theme?: DefaultTheme;
  position?: { x: number; y: number };
  fromFilterScreen?: boolean;
  fromSplitScreen?: boolean;
  showWelcomeScreen: boolean;
  setShowWelcomeScreen: (flag: boolean) => void;
};

const GRADIENT_BORDER_WIDTH = 4;

export const ChatTooltip = memo(
  ({
    isOpen,
    onClose,
    fromFilterScreen = false,
    fromSplitScreen = false,
    showWelcomeScreen,
    setShowWelcomeScreen,
  }: ChatTooltipProps) => {
    const gradientTheme = useTheme();
    const COLOR_1 = gradientTheme["diam-01"] || "#804195";
    const COLOR_2 = gradientTheme["jewelry-01"] || "#58B8A5";
    const GRADIENT_COLORS: readonly [string, string, string, string] = [
      COLOR_1,
      COLOR_2,
      COLOR_1,
      COLOR_2,
    ];
    const { getUserId, getToken, getPriceMode, getUserName } = useUserInfo();
    const linkTo = useLinkTo();
    const chatRef = useRef<ChatWidgetRef>(null);
    const tooltipRef = useRef<View>(null);
    const { trackEvent } = useUserAnalytics() || {};
    const [chatKey, setChatKey] = useState(0);
    const activeProductType = useSelector(
      (state: any) => state.appState?.activeProduct
    );
    const payload = useSearchFiltersPayload(activeProductType);
    const sectionNameRedux = useSelector(
      (state: any) => state.searchFilters.sectionName
    );
    const { bottom } = useSafeAreaInsets();
    const dispatch = useDispatch();
    const aiChatBotConfig = useAIChatBotConfig();
    const { productType } = useContext(FilterScreenContext);
    const { collection: collections } = useSearchFiltersSlice();
    const setPreservedData = !(collections && collections.length > 0);
    const getFilterPayload = useCallback(() => {
      let filterPayload = {};
      if (sectionNameRedux === SearchFilterTypeEnum.PAIRS) {
        filterPayload = {
          ...payload?.[sectionNameRedux],
          pair: "pair",
        };
        return filterPayload;
      } else if (sectionNameRedux === SearchFilterTypeEnum.PARCEL) {
        filterPayload = {
          ...payload?.[sectionNameRedux],
          pair: "parcel",
        };
        return filterPayload;
      }
      return payload?.[sectionNameRedux];
    }, [payload, sectionNameRedux]);

    useFocusEffect(
      useCallback(() => {
        fetchLocalStorageItems("vdbchat_conversations").then((res) => {
          const conversations = JSON.parse((res as string) || "{}");
          setShowWelcomeScreen?.(!conversations?.chat_has_initialized);
        });
        return () => {};
      }, [])
    );

    useEffect(() => {
      const subscription = DeviceEventEmitter.addListener(
        "CLEAR_AI_CHAT",
        () => {
          setChatKey((prev) => prev + 1);
        }
      );
      return () => subscription.remove();
    }, []);

    const handleViewAll = useCallback(
      (deepLinkUrl: string) => {
        RedirectFunc(deepLinkUrl, linkTo);
        trackEvent?.(AIEventNames.AI_CHAT_WIDGET, {
          action: AIEventTypes.SHOW_ALL_RESULTS_CLICKED,
          deepLinkUrl: deepLinkUrl,
        });
        DeviceEventEmitter.emit("AI_FILTERS_APPLIED");
        onClose();
      },
      [linkTo, onClose]
    );

    const handleItemPress = useCallback(
      (deepLinkUrl: string) => {
        RedirectFunc(deepLinkUrl, linkTo);
        trackEvent?.(AIEventNames.AI_CHAT_WIDGET, {
          action: AIEventTypes.PRODUCT_PREVIEW_CLICKED,
          deeplinkUrl: deepLinkUrl,
        });
        onClose();
      },
      [linkTo, onClose]
    );

    const handleClearChat = useCallback(() => {
      trackEvent?.(AIEventNames.AI_CHAT_WIDGET, {
        action: AIEventTypes.CHAT_CLEARED,
      });
      if (
        productType === ProductCategory.JEWELRY ||
        productType === ProductCategory.LABGROWN_JEWELRY
      ) {
        DeviceEventEmitter.emit("RESET_FILTERS_EVENT");
        dispatch(resetJewelryFiltersPayload(setPreservedData));
      } else {
        DeviceEventEmitter.emit("RESET_FILTERS_EVENT");
        dispatch(resetFiltersPayloadByProduct(productType));
      }
      dispatch(clearSubCollections());
      chatRef.current?.clearChat();
    }, []);

    const trackAnalyticsEvent = useCallback(
      (eventName: string, eventData: Record<string, any>) => {
        trackEvent?.(eventName, eventData);
      },
      [trackEvent]
    );

    // Handle clicks outside the tooltip on web
    useEffect(() => {
      if (!isOpen || Platform.OS !== "web") return;

      const handleClickOutside = (event: MouseEvent) => {
        // Check if click is outside the tooltip content
        const tooltipElement = tooltipRef.current as any;
        if (tooltipElement) {
          // For web, we can access the DOM node
          const domNode = tooltipElement._nativeTag || tooltipElement;
          if (domNode && typeof domNode.contains === "function") {
            if (!domNode.contains(event.target)) {
              onClose();
            }
          } else {
            // Fallback: check if click target is not within tooltip
            const target = event.target as HTMLElement;
            const isInsideTooltip = target.closest(
              '[data-tooltip-content="true"]'
            );
            if (!isInsideTooltip) {
              onClose();
            }
          }
        }
      };

      // Add listener with a slight delay to avoid immediate close
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const showGradientBorder = fromFilterScreen && !fromSplitScreen;

    const tooltipContent = (
      <ToolTipContent
        fromFilterScreen={fromFilterScreen}
        fromSplitScreen={fromSplitScreen}
        showGradientBorder={showGradientBorder}
        ref={tooltipRef}
        {...(fromFilterScreen ? {} : { width: TOOLTIP_WIDTH })}
        // @ts-ignore - data attribute for web click outside detection
        data-tooltip-content="true"
      >
        {/* Header */}
        {!fromFilterScreen && !fromSplitScreen && (
          <AiChatHeader
            handleClearChat={handleClearChat}
            handleClose={onClose}
          />
        )}
        <LineViewContainer background={undefined} />

        <ChatContainer>
          <ChatWidget
            key={chatKey}
            ref={chatRef}
            apiUrl={
              Platform.OS === "web" && window.location.hostname !== "localhost"
                ? window.location.protocol + "//" + window.location.host + "/"
                : process.env.EXPO_PUBLIC_API_URL
            }
            theme={{
              primaryColor: "#804195",
              userBubbleColor: "#4F4E57",
              botBubbleColor: "#EDEDF2",
              backgroundColor: "#ffffff",
              fontFamily: "Roboto",
              fontSize: 14,
            }}
            userId={getUserId().toString()}
            userToken={getToken()}
            userName={getUserName()}
            priceMode={getPriceMode().toString()}
            {...(!fromFilterScreen && !fromSplitScreen
              ? { modalHeight: TOOLTIP_HEIGHT - 60 }
              : {})}
            onViewAllPress={handleViewAll}
            onItemPress={handleItemPress}
            isBetaMode={true}
            activeProductType={
              activeProductType === ProductCategory.JEWELRY
                ? sectionNameRedux === SearchFilterTypeEnum.LAB_GROWN_JEWELRY
                  ? "Lab_Grown_Jewelry"
                  : "Jewelry"
                : activeProductType === ProductCategory.LAB_GROWN_DIAMOND
                ? "Lab-Grown"
                : "Natural"
            }
            trackAnalyticsEvent={trackAnalyticsEvent}
            fromFilterScreen={fromFilterScreen}
            fromFilterSplitScreen={fromSplitScreen}
            enableAiAudioRecording={aiChatBotConfig?.enable_ai_audio_recording}
            {...(fromFilterScreen && {
              onClearChat: () => {
                if (
                  productType === ProductCategory.JEWELRY ||
                  productType === ProductCategory.LABGROWN_JEWELRY
                ) {
                  DeviceEventEmitter.emit("RESET_FILTERS_EVENT");
                  dispatch(resetJewelryFiltersPayload(setPreservedData));
                } else {
                  DeviceEventEmitter.emit("RESET_FILTERS_EVENT");
                  dispatch(resetFiltersPayloadByProduct(productType));
                }
                dispatch(clearSubCollections());
                setShowWelcomeScreen(true);
                DeviceEventEmitter.emit("CLEAR_AI_CHAT");
              },
            })}
            {...(payload?.[sectionNameRedux] && {
              classic_search_filters: getFilterPayload(),
            })}
            {...(fromFilterScreen && {
              showWelcomeScreen: showWelcomeScreen,
              setShowWelcomeScreen: (flag) => {
                setShowWelcomeScreen(flag);
              },
            })}
          />
        </ChatContainer>
      </ToolTipContent>
    );

    return (
      <ToolTipContainer
        paddingBottom={bottom}
        pointerEvents="box-none"
        fromFilterScreen={fromFilterScreen}
        fromSplitScreen={fromSplitScreen}
        showWelcomeScreen={showWelcomeScreen}
      >
        {showGradientBorder ? (
          <GradientBorderWrapper showWelcomeScreen={showWelcomeScreen}>
            <LinearGradient
              colors={GRADIENT_COLORS}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
            {tooltipContent}
          </GradientBorderWrapper>
        ) : (
          tooltipContent
        )}
      </ToolTipContainer>
    );
  }
);

const ToolTipContainer = styled.View<{
  fromFilterScreen: boolean;
  fromSplitScreen: boolean;
  paddingBottom: number;
  showWelcomeScreen: boolean;
}>`
  ${({ fromFilterScreen }: { fromFilterScreen: boolean }) =>
    !fromFilterScreen
      ? css`
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 30;
          z-index: 10000;
          align-items: flex-end;
          justify-content: flex-end;
          padding-right: 30;
        `
      : css`
          flex: 1;
        `}
  ${({
    fromFilterScreen,
    fromSplitScreen,
    showWelcomeScreen,
  }: {
    fromFilterScreen: boolean;
    fromSplitScreen: boolean;
    showWelcomeScreen: boolean;
  }) =>
    fromFilterScreen &&
    !fromSplitScreen &&
    css`
      align-items: center;
      justify-content: center;
      align-self: center;
      max-height: 816px;
      min-height: ${showWelcomeScreen ? 298 : 464}px;
      padding-vertical: 24px;
    `};
  padding-bottom: ${({
    paddingBottom,
    fromFilterScreen,
    fromSplitScreen,
    theme,
  }: {
    paddingBottom: number;
    fromFilterScreen: boolean;
    fromSplitScreen: boolean;
    theme: DefaultTheme;
  }) =>
    fromFilterScreen &&
    fromSplitScreen &&
    theme.isTablet &&
    SpaceType.ExtraLarge + paddingBottom}px;
`;

const GradientBorderWrapper = styled.View<{
  showWelcomeScreen: boolean;
}>`
  width: 608px;
  max-height: 816px;
  ${({ showWelcomeScreen }: { showWelcomeScreen: boolean }) =>
    showWelcomeScreen
      ? css`
          height: auto;
        `
      : css`
          height: 100%;
        `};
  min-height: ${({ showWelcomeScreen }: { showWelcomeScreen: boolean }) =>
    showWelcomeScreen ? 298 : 464}px;
  border-radius: 12px;
  overflow: hidden;
  padding: ${GRADIENT_BORDER_WIDTH}px;
`;

const ToolTipContent = styled.View<{
  width?: number;
  fromFilterScreen: boolean;
  fromSplitScreen: boolean;
  showGradientBorder?: boolean;
}>`
  border-radius: ${({ showGradientBorder }: { showGradientBorder?: boolean }) =>
    showGradientBorder ? 12 - GRADIENT_BORDER_WIDTH : 12}px;
  overflow: hidden;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  ${({
    fromFilterScreen,
    theme,
    width,
    fromSplitScreen,
  }: {
    fromFilterScreen: boolean;
    theme: DefaultTheme;
    width?: number;
    fromSplitScreen: boolean;
  }) =>
    !fromFilterScreen &&
    !fromSplitScreen &&
    css`
      height: 85%;
      max-height: 770px;
      box-shadow: ${getBoxShadow(theme.Shadows.Huge)};
      width: ${width}px;
    `}
  ${({
    fromSplitScreen,
    fromFilterScreen,
    showGradientBorder,
  }: {
    fromSplitScreen: boolean;
    fromFilterScreen: boolean;
    showGradientBorder?: boolean;
  }) =>
    fromFilterScreen &&
    !fromSplitScreen &&
    css`
      width: ${showGradientBorder ? 608 - GRADIENT_BORDER_WIDTH * 2 : 608}px;
    `};
  ${({
    fromFilterScreen,
  }: {
    fromSplitScreen: boolean;
    fromFilterScreen: boolean;
  }) =>
    fromFilterScreen &&
    css`
      flex: 1;
    `}
`;

const ChatContainer = styled.View`
  flex: 1;
`;
